import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import useInlineEditBuffer from "@tailormed/common-client/component/inline-edit/hooks/useInlineEditBuffer";
import AssigneesSelectField from "@tailormed/common-client/component/Select/AssigneesSelectField";

import { selectAssigneesAndUserGroups, selectUser } from "../../../store/selector";
import { toAssigneeIdArray } from "../../../utils/formaters";
import getInlineEditPopupContainer from "@tailormed/common-client/component/inline-edit/getInlineEditPopupContainer";

// Shared multi-assignee inline editor used by patient, enrollment and claim assignee cells.
// Multi-select parity with the track tab — commits the selected ids as an array.
const AssigneesInlineEditor = ({ value, onCommit, onCancel, autoFocus = true }) => {
  const { t } = useTranslation();
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const user = useSelector(selectUser);

  const initialIds = toAssigneeIdArray(value)
    .map((id) => (assignees || []).find((a) => a && a.id != null && a.id.toString() === id))
    .filter(Boolean)
    .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""))
    .map(({ id }) => String(id));
  const { buffer, setBuffer, handleBlur, handleKeyDown } = useInlineEditBuffer({
    value: initialIds,
    onCommit,
    onCancel
  });

  return (
    <AssigneesSelectField
      assignees={assignees}
      currentUser={user}
      currentUserSuffix={t("bulk_actions.current_user_suffix")}
      placeholder={t("inline_edit.assignee_placeholder")}
      value={buffer}
      onChange={setBuffer}
      autoFocus={autoFocus}
      defaultOpen={autoFocus}
      suffixIcon={<SearchOutlined />}
      maxTagCount="responsive"
      maxTagPlaceholder={(omitted) => `+${omitted.length}`}
      bordered
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 280, maxHeight: 240, overflowY: "auto" }}
      getPopupContainer={getInlineEditPopupContainer}
      style={{ width: "100%", minWidth: 140 }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

AssigneesInlineEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  onCommit: PropTypes.func,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool
};

export default AssigneesInlineEditor;
